/**
 * ExternalCredentialCard – Visning for credentials importert fra eksterne kilder
 * (i første omgang Vitnemålsportalen via EMREX).
 *
 * Skiller seg fra CertificateCard ved at:
 *  - Henter rik informasjon fra edc_credential JSON-LD (W3C VC + EDC-AP)
 *  - Viser felt som er relevante for høyere utdanning: institusjon, studienivå,
 *    fagfelt (ISCED), akademisk semester, karakter, ECTS
 *  - Linker til institusjonens hjemmeside og emneside hvis tilgjengelig
 *  - Markerer kilden tydelig (Vitnemålsportalen-merke)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ExternalLink, ChevronDown, ChevronUp, Award, Calendar, MapPin, FileText, Hash, Shield, ArrowRight, Trash2 } from 'lucide-react';
import type { Certificate } from '../types';

interface Props {
  certificate: Certificate & { edc_credential?: EdcCredential };
  onDeleted?: (id: string) => void;
}

interface EdcCredential {
  '@context'?: unknown;
  type?: string[];
  id?: string;
  issuer?: {
    preferredName?: { nb?: string; en?: string };
    homepage?: string;
    'kompetanseforbundet:identifiers'?: Record<string, string>;
    location?: {
      address?: {
        countryCode?: { id?: string };
      };
    };
  };
  credentialSubject?: {
    fullName?: { nb?: string };
    hasClaim?: Array<{
      title?: { nb?: string; en?: string };
      ECTSCreditPoints?: number;
      specifiedBy?: {
        title?: { nb?: string; en?: string };
        'kompetanseforbundet:learningType'?: string;
        'kompetanseforbundet:academicLevel'?: string;
        'kompetanseforbundet:identifiers'?: Record<string, string>;
        homepage?: string;
        thematicArea?: Array<{ notation?: string }>;
        ECTSCreditPoints?: number;
      };
      assessment?: {
        grade?: {
          noteLiteral?: { nb?: string; en?: string };
        };
      };
      'kompetanseforbundet:studyPeriod'?: {
        startDate?: string;
        endDate?: string;
        term?: Record<string, string>;
      };
      awardedBy?: {
        awardingDate?: string;
        awardingBody?: {
          preferredName?: { nb?: string; en?: string };
        };
      };
    }>;
  };
  'kompetanseforbundet:source'?: {
    issuingPortal?: string;
    importedAt?: string;
  };
}

const ISCED_LABELS: Record<string, string> = {
  '0311': 'Økonomi',
  '0413': 'Ledelse og administrasjon',
  '0421': 'Jus',
  '0532': 'Kjemi',
  '0588': 'Tverrfaglig naturvitenskap',
  '0612': 'Database og nettverksdesign',
  '0613': 'Programvareutvikling',
  '0731': 'Arkitektur',
  '0811': 'Landbruk',
  '0821': 'Skogbruk',
  '0912': 'Medisin',
  '0913': 'Sykepleie',
  '1031': 'Forsvarsfag'
};

const ACADEMIC_LEVEL_LABEL: Record<string, string> = {
  'Bachelor': 'Bachelorgrad',
  'Master': 'Mastergrad',
  'PhD': 'Doktorgrad'
};

const LEARNING_TYPE_LABEL: Record<string, string> = {
  'Course': 'Emne',
  'Degree Programme': 'Studieprogram',
  'Module': 'Modul'
};

function formatDate(dateStr?: string): string {
  if (!dateStr) return '–';
  try {
    return new Date(dateStr).toLocaleDateString('nb-NO', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return dateStr; }
}

function formatTermPeriod(start?: string, end?: string): string | null {
  if (!start && !end) return null;
  const fmt = (d: string) => new Date(d).toLocaleDateString('nb-NO', { year: 'numeric', month: 'short' });
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  if (start) return `Fra ${fmt(start)}`;
  return `Til ${fmt(end!)}`;
}

export function ExternalCredentialCard({ certificate, onDeleted }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/candidate/vp/imports/${certificate.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Kunne ikke slette vitnemålet');
      }
      onDeleted?.(certificate.id);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Ukjent feil');
      setDeleting(false);
    }
  }
  const cred = certificate.edc_credential;
  const claim = cred?.credentialSubject?.hasClaim?.[0];
  const spec = claim?.specifiedBy;

  const title = claim?.title?.nb || claim?.title?.en || certificate.title;
  const issuerName = cred?.issuer?.preferredName?.nb || cred?.issuer?.preferredName?.en || certificate.sensor_name;
  const issuerHomepage = cred?.issuer?.homepage;
  const academicLevel = spec?.['kompetanseforbundet:academicLevel'];
  const learningType = spec?.['kompetanseforbundet:learningType'];
  const ects = claim?.ECTSCreditPoints || spec?.ECTSCreditPoints || certificate.ects_credits;
  const grade = claim?.assessment?.grade?.noteLiteral?.nb || claim?.assessment?.grade?.noteLiteral?.en;
  const isced = spec?.thematicArea?.[0]?.notation;
  const iscedLabel = isced ? (ISCED_LABELS[isced] || `ISCED ${isced}`) : null;
  const awardingDate = claim?.awardedBy?.awardingDate || certificate.course_completion_date;
  const studyPeriod = claim?.['kompetanseforbundet:studyPeriod'];
  const courseUrl = spec?.homepage;
  const localCode = spec?.['kompetanseforbundet:identifiers']?.local;
  const issuerIds = cred?.issuer?.['kompetanseforbundet:identifiers'] || {};

  const niceLevel = academicLevel ? (ACADEMIC_LEVEL_LABEL[academicLevel] || academicLevel) : null;
  const niceType = learningType ? (LEARNING_TYPE_LABEL[learningType] || learningType) : null;

  return (
    <div className="bg-white border border-blue-200 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Header med VP-merke */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 flex items-center justify-between border-b border-blue-200">
        <div className="flex items-center gap-2 text-xs text-blue-800">
          <Shield className="w-3.5 h-3.5" />
          <span className="font-medium">Vitnemålsportalen</span>
          {niceType && <span className="text-blue-600">· {niceType}</span>}
        </div>
        {niceLevel && (
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
            {niceLevel}
          </span>
        )}
      </div>

      <div className="p-5">
        {/* Tittel */}
        <div className="flex items-start gap-3 mb-3">
          <div className="bg-blue-50 p-2 rounded">
            <GraduationCap className="w-5 h-5 text-blue-700" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 leading-snug" title={title}>
              {title}
            </h3>
            {iscedLabel && (
              <p className="text-xs text-gray-500 mt-0.5">{iscedLabel}</p>
            )}
          </div>
        </div>

        {/* Hovedfakta */}
        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Kompetansepoeng</div>
            <div className="font-medium text-gray-900">{ects ? `${ects} ECTS` : '–'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Karakter</div>
            <div className="font-medium text-gray-900">{grade || 'Bestått'}</div>
          </div>
        </div>

        {/* Utsteder */}
        <div className="flex items-start gap-2 text-sm text-gray-700 mb-3 pb-3 border-b border-gray-100">
          <MapPin className="w-4 h-4 mt-0.5 text-gray-400" />
          <div className="flex-1">
            <div className="font-medium">{issuerName}</div>
            {issuerHomepage && (
              <a
                href={issuerHomepage}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                {issuerHomepage.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>

        {/* Datoer */}
        <div className="space-y-1.5 text-xs text-gray-600 mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span>Tildelt {formatDate(awardingDate)}</span>
          </div>
          {(() => {
            const period = formatTermPeriod(studyPeriod?.startDate, studyPeriod?.endDate);
            return period && (
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                <span>Studieperiode: {period}</span>
              </div>
            );
          })()}
        </div>

        {/* Knapprad */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            aria-expanded={expanded}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium cursor-pointer select-none py-1"
          >
            {expanded ? (<>Vis mindre <ChevronUp className="w-3 h-3" /></>) : (<>Vis flere detaljer <ChevronDown className="w-3 h-3" /></>)}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
              className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1.5 rounded-md font-medium inline-flex items-center gap-1 whitespace-nowrap"
              title="Fjern dette vitnemålet fra lommeboken"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Fjern
            </button>
            <Link
              to={`/external-credentials/${certificate.id}`}
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1 whitespace-nowrap"
            >
              Se hele vitnemålet
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* Slett-bekreftelse */}
        {confirmingDelete && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !deleting && setConfirmingDelete(false)}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-red-100 p-2 rounded-full flex-shrink-0">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Fjern vitnemål fra lommeboken?</h3>
                  <p className="text-sm text-gray-600">
                    "{title}" vil bli fjernet. Du kan importere det på nytt fra Vitnemålsportalen senere hvis du ombestemmer deg.
                  </p>
                </div>
              </div>
              {deleteError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {deleteError}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {deleting ? 'Fjerner…' : 'Ja, fjern'}
                </button>
              </div>
            </div>
          </div>
        )}

        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2.5 text-xs">
            {/* Identifikatorer for emnet */}
            {localCode && (
              <div className="flex items-start gap-2">
                <Hash className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                <div>
                  <span className="text-gray-500">Lokal kode:</span>{' '}
                  <span className="font-mono text-gray-700">{localCode}</span>
                </div>
              </div>
            )}

            {/* Lenke til emneside */}
            {courseUrl && (
              <div className="flex items-start gap-2">
                <FileText className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                <a
                  href={courseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  Emneside hos institusjonen
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {/* Institusjons-identifikatorer (filter ut ikke-streng-verdier
                som forsvar mot XML-parsing-artefakter) */}
            {(() => {
              const safeIds = Object.entries(issuerIds).filter(
                ([_, v]) => typeof v === 'string' && v.length > 0
              );
              if (safeIds.length === 0) return null;
              return (
                <div className="flex items-start gap-2">
                  <Award className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-gray-500 mb-1">Institusjons-koder:</div>
                    <div className="space-y-0.5 font-mono text-gray-700">
                      {safeIds.map(([type, value]) => (
                        <div key={type}>
                          <span className="text-gray-500">{type}:</span> {String(value)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Sertifikat-ID */}
            <div className="flex items-start gap-2">
              <FileText className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
              <div>
                <span className="text-gray-500">Sertifikat-nr:</span>{' '}
                <span className="font-mono text-gray-700">{certificate.certificate_number}</span>
              </div>
            </div>

            {/* Hash av signert ELMO (sporbarhet) */}
            {(cred?.id) && (
              <div className="flex items-start gap-2">
                <Shield className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                <div className="break-all">
                  <span className="text-gray-500">Credential-ID:</span>{' '}
                  <span className="font-mono text-gray-700 text-[10px]">{cred.id}</span>
                </div>
              </div>
            )}

            {/* Bunnlinje – alltid synlig når ekspandert */}
            <div className="text-gray-500 pt-2 border-t border-gray-100">
              <div>
                Hentet fra Vitnemålsportalen
                {certificate.external_imported_at && ` · ${formatDate(certificate.external_imported_at)}`}
              </div>
              <div className="mt-0.5">
                Signert digitalt av Sikt og verifisert mot EMREX-registry
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
